/**
 * useAssociatedSymptoms
 *
 * sx_collect_associated 타입을 위한 연관 증상 키 추출 훅
 * V1 use-tree-helpers.ts의 getAssociatedSymptomKeys 로직 포팅
 *
 * 연관 증상이란:
 * - 현재 선택된 증상 트리들에서 sx_select 질문의 answerKeys
 * - 예: "Chest pain" 증상을 선택하면, 그 트리 내 sx_select 질문의 옵션들
 *   (예: "Shortness of breath", "Nausea" 등)이 연관 증상이 됨
 */

import { useState, useEffect, useMemo } from 'react';
import { difference, uniq, isEmpty } from 'lodash';
import { questionTreeControllerFindByTreeKey } from '../../../../api/generated/question-trees/question-trees';
import {
  questionControllerFindById,
  questionControllerFindByTreeId,
} from '../../../../api/generated/questions/questions';
import { conditionHelpers } from '../../../../../report/helper-functions';

// "None of the above" 키 상수
const NONE_ABOVE_KEY = 'o-none-above';
const OTHER_KEY = 'o-other';
const associatedTreeCache = new Map<
  string,
  { keys: string[]; optionsMap: Record<string, { label: string; dataType?: string }> }
>();

export interface UseAssociatedSymptomsProps {
  /** 이미 선택된 증상 트리 키들 (Container에서 전달) */
  selectedSymptomTreeKeys: string[];
  /** Survey ID */
  surveyId?: number;
  /** 로케일 */
  locale: string;
  /** 활성화 여부 */
  enabled?: boolean;
  /** optionConditions 계산용 리포트 상태 (V1 parity) */
  reportState?: unknown;
  /** optionConditions 계산용 설문 데이터 (V1 parity) */
  survey?: unknown;
}

export interface UseAssociatedSymptomsReturn {
  /** 연관 증상 키 목록 */
  associatedSymptomKeys: string[];
  /** 연관 증상 옵션 데이터 (key -> label 등) */
  associatedSymptomOptions: Record<string, { label: string; dataType?: string }>;
  /** 로딩 중 여부 */
  isLoading: boolean;
  /** 에러 */
  error: string | null;
}

/**
 * 증상 트리에서 sx_select 질문의 answerKeys 추출
 */
async function fetchSxSelectAnswerKeysFromTree(
  treeKey: string,
  surveyId: number,
  locale: string,
  options?: {
    reportState?: unknown;
    survey?: unknown;
    contextSignature?: string;
  }
): Promise<{ keys: string[]; optionsMap: Record<string, { label: string; dataType?: string }> }> {
  const cacheKey = `${surveyId}:${locale}:${treeKey}:${options?.contextSignature || 'default'}`;
  const cached = associatedTreeCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    // treeKey로 트리 찾기
    const tree = await questionTreeControllerFindByTreeKey({
      surveyId,
      treeKey,
    });

    if (!tree) {
      return { keys: [], optionsMap: {} };
    }

    // 트리의 질문들 로드
    const questions = await questionControllerFindByTreeId(tree.id, { locale });

    // sx_select 타입 질문에서 answerKeys 추출
    const sxSelectQuestions = questions.filter((q) => q.inputType === 'sx_select');

    const details = await Promise.all(
      sxSelectQuestions.map((q) =>
        questionControllerFindById(q.id).catch(() => null)
      )
    );
    const detailMap = new Map<number, any>();
    details.forEach((detail) => {
      if (detail?.id) {
        detailMap.set(detail.id, detail);
      }
    });

    const getRefinedAnswerKeys = (
      answerKeys: string[],
      optionConditions?: Array<any> | null,
      question?: any
    ): string[] => {
      if (!answerKeys || answerKeys.length === 0) return [];
      if (!optionConditions || optionConditions.length === 0 || !options?.reportState) {
        return answerKeys;
      }

      let exclusionMatches: string[] = [];
      let inclusionMatches: string[] = [];
      let inclusionFailures: string[] = [];

      optionConditions.forEach((condition) => {
        let matched = false;
        if (condition?.func) {
          const helper =
            conditionHelpers[condition.func as keyof typeof conditionHelpers];
          if (helper) {
            try {
              matched = Boolean(
                helper({
                  question,
                  reportState: options.reportState as any,
                  survey: options.survey as any,
                  condition,
                })
              );
            } catch {
              matched = false;
            }
          }
        }

        if (matched && condition.type === 'exclude') {
          exclusionMatches = [...exclusionMatches, ...((condition.keys as string[]) || [])];
        }

        if (condition.type === 'include') {
          if (matched) {
            inclusionMatches = [...inclusionMatches, ...((condition.keys as string[]) || [])];
          } else {
            inclusionFailures = [...inclusionFailures, ...((condition.keys as string[]) || [])];
          }
        }
      });

      const keysToRemove = new Set([
        ...exclusionMatches,
        ...difference(inclusionFailures, inclusionMatches),
      ]);

      if (keysToRemove.size === 0) {
        return answerKeys;
      }

      return answerKeys.filter((key) => !keysToRemove.has(key));
    };

    const keys: string[] = [];
    const optionsMap: Record<string, { label: string; dataType?: string }> = {};

    sxSelectQuestions.forEach((q) => {
      const detail = detailMap.get(q.id);
      const refinedAnswerKeys = getRefinedAnswerKeys(
        q.answerKeys || [],
        detail?.optionConditions as Array<any> | undefined,
        detail || q
      );
      keys.push(...refinedAnswerKeys);

      // 옵션 정보 저장
      refinedAnswerKeys.forEach((key) => {
        const optionData = q.answerOptionsMap?.[key] as Record<string, unknown> | undefined;
        optionsMap[key] = {
          label: (optionData?.label as string) || key,
          dataType: optionData?.dataType as string | undefined,
        };
      });
    });

    const result = { keys: uniq(keys), optionsMap };
    associatedTreeCache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error(`Failed to fetch sx_select keys from tree ${treeKey}:`, error);
    return { keys: [], optionsMap: {} };
  }
}

export function useAssociatedSymptoms({
  selectedSymptomTreeKeys,
  surveyId,
  locale,
  enabled = true,
  reportState,
  survey,
}: UseAssociatedSymptomsProps): UseAssociatedSymptomsReturn {
  const normalizedSymptomTreeKeys = useMemo(
    () =>
      uniq((selectedSymptomTreeKeys || []).filter((key) => key && key.startsWith('sx-'))).sort(),
    [selectedSymptomTreeKeys]
  );
  const normalizedSymptomTreeKeysSignature = useMemo(
    () => normalizedSymptomTreeKeys.join('|'),
    [normalizedSymptomTreeKeys]
  );

  const [associatedSymptomKeys, setAssociatedSymptomKeys] = useState<string[]>([]);
  const [associatedSymptomOptions, setAssociatedSymptomOptions] = useState<
    Record<string, { label: string; dataType?: string }>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const conditionContextSignature = useMemo(() => {
    if (!reportState) return 'no-context';
    try {
      const reportStateObj = reportState as Record<string, unknown>;
      const treeStates = (reportStateObj.treeStates as Array<Record<string, unknown>> | undefined) || [];
      const relevantTreeStates = treeStates.filter((treeState) =>
        normalizedSymptomTreeKeys.includes(String(treeState.treeKey || ''))
      );
      return JSON.stringify({
        systemValueMap: reportStateObj.systemValueMap || {},
        treeStates: relevantTreeStates.map((treeState) => ({
          treeKey: treeState.treeKey,
          answerMap: treeState.answerMap,
        })),
      });
    } catch {
      return 'context-unserializable';
    }
  }, [normalizedSymptomTreeKeys, reportState]);
  const requestSignature = useMemo(
    () =>
      `${surveyId || 0}:${locale}:${normalizedSymptomTreeKeysSignature}:${conditionContextSignature}`,
    [surveyId, locale, normalizedSymptomTreeKeysSignature, conditionContextSignature]
  );
  const [lastLoadedSignature, setLastLoadedSignature] = useState<string>('');

  // 선택된 증상들에서 연관 증상 추출
  useEffect(() => {
    if (!enabled || !surveyId || isEmpty(normalizedSymptomTreeKeys)) {
      setIsLoading(false);
      setError(null);
      setAssociatedSymptomKeys([]);
      setAssociatedSymptomOptions({});
      setLastLoadedSignature('');
      return undefined;
    }
    if (lastLoadedSignature === requestSignature) {
      // Prevent stale loading spinner when the same request signature re-enters.
      setIsLoading(false);
      return undefined;
    }

    // surveyId가 여기서는 확실히 number임
    const currentSurveyId = surveyId;
    let isCancelled = false;

    async function fetchAssociatedSymptoms() {
      setIsLoading(true);
      setError(null);

      try {
        const allKeys: string[] = [];
        const allOptions: Record<string, { label: string; dataType?: string }> = {};

        // 각 선택된 증상 트리에서 sx_select answerKeys 추출
        await Promise.all(
          normalizedSymptomTreeKeys.map(async (sxKey) => {
            const { keys, optionsMap } = await fetchSxSelectAnswerKeysFromTree(
              sxKey,
              currentSurveyId,
              locale,
              {
                reportState,
                survey,
                contextSignature: conditionContextSignature,
              }
            );
            allKeys.push(...keys);
            Object.assign(allOptions, optionsMap);
          })
        );

        if (isCancelled) return;

        // Exclude already selected primary symptoms from associated options.
        const filteredKeys = difference(
          uniq(allKeys),
          [NONE_ABOVE_KEY, OTHER_KEY, ...normalizedSymptomTreeKeys]
        );

        setAssociatedSymptomKeys((prev) => {
          const next = filteredKeys;
          if (prev.length === next.length && prev.every((v, idx) => v === next[idx])) {
            return prev;
          }
          return next;
        });
        setAssociatedSymptomOptions((prev) => {
          const prevKeys = Object.keys(prev);
          const nextKeys = Object.keys(allOptions);
          if (
            prevKeys.length === nextKeys.length &&
            prevKeys.every((key) => {
              const p = prev[key];
              const n = allOptions[key];
              return p?.label === n?.label && p?.dataType === n?.dataType;
            })
          ) {
            return prev;
          }
          return allOptions;
        });
        setLastLoadedSignature(requestSignature);
      } catch (err) {
        if (isCancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch associated symptoms');
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchAssociatedSymptoms();

    return () => {
      isCancelled = true;
    };
  }, [
    enabled,
    surveyId,
    locale,
    normalizedSymptomTreeKeysSignature,
    requestSignature,
    lastLoadedSignature,
    reportState,
    survey,
    conditionContextSignature,
  ]);

  return {
    associatedSymptomKeys,
    associatedSymptomOptions,
    isLoading,
    error,
  };
}
